import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  TextField,
  Tooltip,
  IconButton,
  Collapse
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import TuneIcon from '@mui/icons-material/Tune';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { extractPackageFromDocument } from '../../api/packageAPI';

const QUICK_PROMPTS = [
  { label: '📍 Region: Sikkim', text: 'Region / Location is Sikkim. ' },
  { label: '📍 Region: Darjeeling', text: 'Region / Location is Darjeeling. ' },
  { label: '📍 Region: Meghalaya', text: 'Region / Location is Meghalaya. ' },
  { label: '📅 5 Days / 4 Nights', text: 'Duration is 5 Days / 4 Nights. ' },
  { label: '📅 6 Days / 5 Nights', text: 'Duration is 6 Days / 5 Nights. ' },
  { label: '💰 Standard: ₹18,000 / Deluxe: ₹24,000', text: 'Pricing for 2 pax is Standard ₹18,000 and Deluxe ₹24,000. ' },
  { label: '🎒 Standard Checklist', text: 'Include comprehensive high altitude things to carry checklist. ' },
];

function getFileIcon(fileName = '') {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.pdf')) {
    return <PictureAsPdfIcon sx={{ fontSize: 18, color: '#ef4444' }} />;
  }
  if (lower.endsWith('.doc') || lower.endsWith('.docx')) {
    return <DescriptionIcon sx={{ fontSize: 18, color: '#2563eb' }} />;
  }
  return <ArticleIcon sx={{ fontSize: 18, color: '#64748b' }} />;
}

function formatFileSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const PackageAiUpload = ({ onExtracted }) => {
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [promptText, setPromptText] = useState('');
  const [showPromptBox, setShowPromptBox] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [lastExtractedPkg, setLastExtractedPkg] = useState(null);

  const fileInputRef = useRef(null);

  const handleFilesAdded = (filesList) => {
    if (!filesList || filesList.length === 0) return;

    const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.md'];
    const valid = Array.from(filesList).filter((f) => {
      const name = f.name.toLowerCase();
      return allowedExtensions.some((ext) => name.endsWith(ext));
    });

    if (valid.length === 0) {
      setErrorMsg('Please upload valid document(s): PDF (.pdf), Word (.docx, .doc), or Text (.txt).');
      return;
    }

    setErrorMsg(null);
    setStatusMsg(null);

    setSelectedFiles((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const next = [...prev];
      for (const f of valid) {
        if (!existingNames.has(f.name)) {
          next.push(f);
          existingNames.add(f.name);
        }
      }
      return next;
    });
  };

  const handleRemoveFile = (idx) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleClearAll = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleExecuteExtraction = async () => {
    if (selectedFiles.length === 0 && !promptText.trim()) {
      setErrorMsg('Please select at least one document (PDF, DOCX, DOC) or enter custom prompt instructions.');
      return;
    }

    const totalBytes = selectedFiles.reduce((acc, f) => acc + f.size, 0);
    if (totalBytes > 50 * 1024 * 1024) {
      setErrorMsg('Total file size exceeds 50 MB limit. Please remove some files.');
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    setStatusMsg(null);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });

      if (promptText.trim()) {
        formData.append('prompt', promptText.trim());
        formData.append('customPrompt', promptText.trim());
      }

      const res = await extractPackageFromDocument(formData);
      const pkg = res?.data?.data || res?.data;

      if (pkg && (pkg.label || pkg.details)) {
        setLastExtractedPkg(pkg);
        const dayCount = pkg.details?.itinerary?.length || pkg.duration || 0;
        const msg = `Successfully auto-filled "${pkg.label || 'Tour Package'}" (${dayCount} Days) across all form sections!`;
        setStatusMsg(msg);

        if (typeof onExtracted === 'function') {
          onExtracted(pkg);
        }
      } else {
        setErrorMsg(res?.data?.message || 'Could not parse complete package details from document.');
      }
    } catch (err) {
      console.error('Package AI extraction error:', err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to process document with Gemini AI. Please check your document and try again.';
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickChip = (item) => {
    setPromptText((prev) => {
      const trimmed = prev.trim();
      return trimmed ? `${trimmed}\n${item.text}` : item.text;
    });
    setShowPromptBox(true);
  };

  const hasInputs = selectedFiles.length > 0 || promptText.trim().length > 0;

  return (
    <Paper
      elevation={0}
      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          handleFilesAdded(e.dataTransfer.files);
        }
      }}
      sx={{
        p: 2.2,
        mb: 3,
        borderRadius: 2.5,
        background: 'linear-gradient(135deg, #f8faff 0%, #f5f3ff 100%)',
        border: dragActive ? '2px dashed #6366f1' : '1.5px dashed #c7d2fe',
        boxShadow: '0 2px 10px rgba(99, 102, 241, 0.06)',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: '#818cf8',
        },
      }}
    >
      {/* ── Header row ──────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: isExpanded ? 1.5 : 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
              Gemini AI Auto-Fill from Document
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontSize: 12 }}>
              Upload itinerary PDF or Word doc (.docx, .doc, .txt) to automatically extract and populate all fields
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={isExpanded ? 'Collapse AI panel' : 'Expand AI panel'}>
            <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)} sx={{ color: '#64748b' }}>
              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        {/* ── Status / Error alerts ───────────────────────────────────── */}
        {errorMsg && (
          <Alert
            severity="error"
            onClose={() => setErrorMsg(null)}
            sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}
          >
            {errorMsg}
          </Alert>
        )}

        {statusMsg && (
          <Alert
            severity="success"
            icon={<CheckCircleOutlineIcon fontSize="inherit" />}
            onClose={() => setStatusMsg(null)}
            sx={{ mb: 2, borderRadius: 2, fontSize: 13, bgcolor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}
          >
            {statusMsg}
          </Alert>
        )}

        {/* ── Upload & Action row ─────────────────────────────────────── */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
          <input
            type="file"
            ref={fileInputRef}
            multiple
            accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                handleFilesAdded(e.target.files);
              }
            }}
            style={{ display: 'none' }}
          />

          <Button
            variant="contained"
            component="span"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            startIcon={<ArticleIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
              fontSize: 13,
              px: 2.2,
              py: 0.9,
              bgcolor: '#4f46e5',
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
              '&:hover': { bgcolor: '#4338ca' },
            }}
          >
            Choose PDF or Word Doc
          </Button>

          <Button
            variant="outlined"
            onClick={() => setShowPromptBox(!showPromptBox)}
            startIcon={<TuneIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: 13,
              px: 2,
              py: 0.8,
              borderColor: showPromptBox ? '#4f46e5' : '#cbd5e1',
              color: showPromptBox ? '#4f46e5' : '#475569',
              bgcolor: showPromptBox ? '#eef2ff' : '#fff',
              '&:hover': { borderColor: '#4f46e5', bgcolor: '#f5f3ff' },
            }}
          >
            {showPromptBox ? 'Hide Custom Prompt' : 'Add Custom Prompt / Override'}
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="contained"
            disabled={loading || !hasInputs}
            onClick={handleExecuteExtraction}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />
            }
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
              fontSize: 13.5,
              px: 3,
              py: 0.9,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 15px rgba(99, 102, 241, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              },
            }}
          >
            {loading ? 'Analyzing with Gemini AI...' : 'Auto-Fill Package'}
          </Button>
        </Box>

        {/* ── Selected files chips ────────────────────────────────────── */}
        {selectedFiles.length > 0 && (
          <Box sx={{ mb: 1.5, p: 1.2, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Attached Document(s) ({selectedFiles.length})
              </Typography>
              <Button
                size="small"
                onClick={handleClearAll}
                startIcon={<ClearIcon fontSize="inherit" />}
                sx={{ textTransform: 'none', fontSize: 11, color: '#ef4444', p: 0, minWidth: 0 }}
              >
                Clear all
              </Button>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.8 }}>
              {selectedFiles.map((f, i) => (
                <Chip
                  key={i}
                  icon={getFileIcon(f.name)}
                  label={`${f.name} (${formatFileSize(f.size)})`}
                  onDelete={() => handleRemoveFile(i)}
                  deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                  size="small"
                  sx={{
                    bgcolor: '#f1f5f9',
                    borderRadius: 1.5,
                    border: '1px solid #cbd5e1',
                    fontSize: 12,
                    fontWeight: 500,
                    maxWidth: 320,
                    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* ── Custom Prompt Drawer / Box ──────────────────────────────── */}
        <Collapse in={showPromptBox}>
          <Box sx={{ mt: 1, p: 1.8, bgcolor: '#ffffff', borderRadius: 2, border: '1px solid #e0e7ff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
              <LightbulbOutlinedIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b' }}>
                Optional Instructions / Overrides for Gemini AI:
              </Typography>
            </Box>

            <TextField
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="e.g. Set region to Sikkim, duration 6 days. Include Tiger Hill sunrise and Ravangla Buddha Park. Pricing for 2 pax: Standard 18500, Deluxe 24500..."
              fullWidth
              size="small"
              multiline
              minRows={2}
              maxRows={5}
              sx={{
                bgcolor: '#fafafa',
                fontSize: 13,
                '& .MuiOutlinedInput-root': { borderRadius: 1.5 },
              }}
            />

            {/* Quick Suggestions Chips */}
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: 11, display: 'block', mb: 0.5 }}>
                Quick helpers (click to insert):
              </Typography>
              <Stack direction="row" spacing={0.8} sx={{ flexWrap: 'wrap', gap: 0.6 }}>
                {QUICK_PROMPTS.map((q, idx) => (
                  <Chip
                    key={idx}
                    label={q.label}
                    size="small"
                    onClick={() => handleQuickChip(q)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      fontSize: 11,
                      fontWeight: 500,
                      color: '#475569',
                      '&:hover': { bgcolor: '#eef2ff', borderColor: '#c7d2fe', color: '#4f46e5' },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        </Collapse>

        {/* ── Last extracted badge summary ────────────────────────────── */}
        {lastExtractedPkg && (
          <Box sx={{ mt: 1.5, p: 1.2, bgcolor: '#ecfdf5', borderRadius: 2, border: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#065f46' }}>
              Populated Fields:
            </Typography>
            <Chip label={`Label: ${lastExtractedPkg.label || 'N/A'}`} size="small" sx={{ bgcolor: '#d1fae5', color: '#047857', fontSize: 11 }} />
            <Chip label={`Region: ${lastExtractedPkg.location || 'N/A'}`} size="small" sx={{ bgcolor: '#d1fae5', color: '#047857', fontSize: 11 }} />
            <Chip label={`Duration: ${lastExtractedPkg.duration || 0}D`} size="small" sx={{ bgcolor: '#d1fae5', color: '#047857', fontSize: 11 }} />
            <Chip label={`Itinerary: ${lastExtractedPkg.details?.itinerary?.length || 0} Days`} size="small" sx={{ bgcolor: '#d1fae5', color: '#047857', fontSize: 11 }} />
            <Chip label={`Inclusions: ${lastExtractedPkg.details?.cost?.inclusions?.length || 0}`} size="small" sx={{ bgcolor: '#d1fae5', color: '#047857', fontSize: 11 }} />
            <Chip label={`FAQs: ${lastExtractedPkg.faqs?.length || 0}`} size="small" sx={{ bgcolor: '#d1fae5', color: '#047857', fontSize: 11 }} />
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default PackageAiUpload;

